import { useState, useEffect, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useOptionalNavigate } from 'hooks/useOptionalNavigate'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useStore } from 'hooks/useStore'
import { useAuth } from 'hooks/useAuth'
import { Alert } from 'components/common'
import { Button } from 'components/atoms/button'
import { CodeInput } from 'components/atoms/code-input'
import { OTP_RESEND_COUNTDOWN_SECONDS } from 'utils/constants'

const validationSchema = Yup.object().shape({
	otp: Yup.string().required('').min(6, ''),
})

interface TwoFAVerificationFormProps {
	urlOtp?: string | null
	urlToken?: string | null
	isModal?: boolean
	onVerified?: () => void
}

export function TwoFAVerificationForm({
	urlOtp = null,
	urlToken = null,
	isModal = false,
	onVerified,
}: TwoFAVerificationFormProps) {
	const navigate = useOptionalNavigate()
	const { verifyOtp, resendOtp, verifyEmailToken, logout } = useAuth()
	const { clearStore } = useStore()
	const [message, setMessage] = useState<any>(null)
	const [submitting, setSubmitting] = useState(false)
	const [resending, setResending] = useState(false)
	// An OTP is already sent when this form is shown, so the cooldown starts immediately.
	const [countdown, setCountdown] = useState(OTP_RESEND_COUNTDOWN_SECONDS)
	const {
		control,
		handleSubmit,
		formState: { errors, isValid },
		getValues,
		reset,
	} = useForm({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
	})

	useEffect(() => {
		if (urlOtp) {
			reset({ otp: urlOtp })
		}
	}, [urlOtp, reset])

	useEffect(() => {
		if (countdown > 0) {
			const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
			return () => clearTimeout(timer)
		}
	}, [countdown])

	useEffect(() => {
		if (urlToken) {
			verifyEmailToken(
				urlToken,
				() => {
					setSubmitting(false)
					if (onVerified) {
						onVerified()
					}
				},
				(error: string) => {
					setSubmitting(false)
					setMessage({
						description: error,
						type: 'danger',
					})
				}
			)
			setMessage({
				description: 'Click continue to log in.',
				type: 'info',
				title: `You're verified!`,
			})
			reset({ otp: urlToken }) // Token as otp
		}
	}, [urlToken, verifyEmailToken, reset, onVerified])

	// The code auto-submits on its 6th digit, so Enter or a click can land in the same tick
	const verifyInFlightRef = useRef(false)
	const finishVerify = () => {
		verifyInFlightRef.current = false
		setSubmitting(false)
	}

	const doVerifyOtp = (otp: string) => {
		if (verifyInFlightRef.current) return
		verifyInFlightRef.current = true
		setSubmitting(true)
		setMessage(null)

		if (otp && otp.length > 6) {
			verifyEmailToken(
				otp,
				() => {
					finishVerify()
					if (onVerified) {
						onVerified()
					}
				},
				(error: string) => {
					finishVerify()
					setMessage({
						description: error,
						type: 'danger',
					})
				}
			)
		} else {
			verifyOtp(
				otp,
				'VerifySignIn',
				() => {
					finishVerify()
					if (onVerified) {
						onVerified()
					}
				},
				(error: string) => {
					finishVerify()
					// Cleared so the next code can be typed (and auto-submitted) from the start
					reset({ otp: '' })
					setMessage({
						description: error,
						type: 'danger',
					})
				}
			)
		}
	}

	const doResendOtp = () => {
		setResending(true)
		setMessage(null)
		setCountdown(OTP_RESEND_COUNTDOWN_SECONDS)

		resendOtp(
			'VerifySignIn',
			() => {
				setResending(false)
				setMessage({
					description: 'New OTP was resent',
					type: 'info',
				})
			},
			(error: string) => {
				setResending(false)
				setMessage({
					description: error,
					type: 'danger',
				})
			}
		)
	}

	const onSubmit = (values: any) => {
		doVerifyOtp(values.otp)
	}
	const handleLogout = () => {
		logout(null, null, () => {
			clearStore()
			if (!isModal) {
				// Only try to use navigate if we're not in modal mode
				navigate('/auth/login', { replace: true })
			} else {
				window.location.replace('/auth/login')
			}
		})
	}

	if (urlToken) {
		return (
			<div className="flex justify-center items-center py-4">
				<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin className="text-white" />
			</div>
		)
	}

	return (
		<div
			className={clsx({
				'w-full': !isModal,
			})}
		>
			{!isModal && (
				<div className="flex justify-end mb-4">
					<button
						className="px-6 py-2 rounded-3xl text-center transition-all duration-300 bg-white/10 text-white font-bold block h-[40px] shadow-button backdrop-blur-lg hover:bg-white-25 disabled:bg-white/10 disabled:text-white-25 disabled:shadow-none"
						onClick={handleLogout}
						type="button"
						disabled={submitting}
					>
						Sign Up
					</button>
				</div>
			)}
			<div className="w-full">
				<h1
					className={clsx('text-3xl font-bold text-center text-white mb-6', {
						invisible: !!urlToken,
					})}
				>
					2FA verification
				</h1>
				<div className="text-white text-center mb-4">
					{clsx({ 'Enter your code': !urlToken, 'Account Confirmation': !!urlToken })}
				</div>
				<Alert message={message} />
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="space-y-4">
						{!(getValues('otp')?.length > 6) && (
							<div>
								<Controller
									control={control}
									name="otp"
									render={({ field }) => (
										<CodeInput
											ref={field.ref}
											aria-label="6-digit verification code"
											// Mounts with the form, so focus lands on it on arrival
											autoFocus
											value={field.value ?? ''}
											onChange={value => {
												field.onChange(value)
												// Same as the email code: the 6th digit submits
												if (value.length === 6 && !submitting) void handleSubmit(onSubmit)()
											}}
											onBlur={field.onBlur}
											// Read-only, not disabled, while verifying: keeps focus (and the mobile keyboard) for the next try
											readOnly={submitting}
										/>
									)}
								/>
								{errors.otp && (
									<div className="text-secondary-red mt-2 text-sm">
										{errors.otp.message as string}
									</div>
								)}
							</div>
						)}
					</div>
					<div className="mt-6">
						<Button
							variant="primary"
							fullWidth
							size="lg"
							type="submit"
							className="flex items-center justify-center gap-1"
							disabled={!isValid || submitting}
						>
							{submitting && <FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin />}
							Continue
						</Button>
					</div>
				</form>
				<div className="mt-5">
					<div className="text-center text-white font-bold">
						Troubles logging in?{' '}
						<Button className="inline-flex" variant="link" onClick={handleLogout} disabled={submitting}>
							Contact us
						</Button>
					</div>
				</div>
				<div className="mt-16">
					<h3 className="text-center text-white mb-6">Didn't get the code?</h3>
					<div className="text-center">
						<Button
							variant="outline"
							fullWidth
							size="lg"
							className="flex items-center justify-center gap-1"
							onClick={() => doResendOtp()}
							disabled={resending || countdown > 0}
						>
							{resending && <FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin />}
							Resend {countdown > 0 && !resending && `(${countdown}s)`}
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
